import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import classNames from "classnames/bind";
import styles from "./ImageGallery.module.scss";

const cx = classNames.bind(styles);

const AUTO_PLAY_INTERVAL = 4000; // ms, chỉnh thời gian tự chuyển ảnh ở đây

const ImageGallery = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(true);

  // Touch state
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  // Auto play interval ref
  const autoPlayRef = useRef();

  const nextImage = React.useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setCurrentIndex((prev) => (prev + 1) % images.length);
    },
    [images.length]
  );

  const prevImage = React.useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    },
    [images.length]
  );

  const goToImage = (index) => {
    setCurrentIndex(index);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") nextImage();
    if (e.key === "ArrowLeft") prevImage();
    if (e.key === "Escape") setIsFullscreen(false);
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const delta = touchStartX.current - touchEndX.current;
      if (Math.abs(delta) > 40) {
        if (delta > 0) {
          nextImage();
        } else {
          prevImage();
        }
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Auto play effect
  useEffect(() => {
    autoPlayRef.current = nextImage;
  });

  useEffect(() => {
    if (!isFullscreen) {
      const play = () => {
        autoPlayRef.current();
      };
      const interval = setInterval(play, AUTO_PLAY_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [isFullscreen, nextImage]);

  // Keyboard navigation in fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isFullscreen, nextImage, prevImage]);

  return (
    <div className={cx("imageGallery")}>
      {/* Main Gallery Container */}

      <div
        className={cx("container")}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Main Image Display */}
        <div className={cx("mainDisplay")} onClick={toggleFullscreen}>
          <img
            src={images[currentIndex].original}
            alt={images[currentIndex].description}
            loading="lazy"
          />

          {/* Navigation Arrows */}
          <button onClick={prevImage} className={cx("navButton", "left")}>
            <ChevronLeft size={24} />
          </button>

          <button onClick={nextImage} className={cx("navButton", "right")}>
            <ChevronRight size={24} />
          </button>

          {/* Image Counter - center */}
          <div className={cx("counter", "center")}>
            {currentIndex + 1} / {images.length}
          </div>
        </div>

        {/* Thumbnails */}
        {showThumbnails && (
          <div className={cx("thumbnails")}>
            <div className={cx("thumbnailsContainer")}>
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => goToImage(index)}
                  className={cx("thumbnail", {
                    active: index === currentIndex,
                  })}
                >
                  <img src={image.thumbnail} alt={`Thumbnail ${index + 1}`}  loading="lazy"/>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Description */}
      <div className={cx("description")}>
        <p>{images[currentIndex].description}</p>
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div
          className={cx("fullscreenModal")}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className={cx("modalContainer")}>
            <img
            loading="lazy"
              src={images[currentIndex].original}
              alt={images[currentIndex].description}
              className={cx("modalImage")}
            />

            {/* Close Button */}
            <button onClick={toggleFullscreen} className={cx("closeButton")}>
              <X size={24} />
            </button>

            {/* Navigation in Fullscreen */}
            <button
              onClick={prevImage}
              className={cx("modalNavButton", "left")}
            >
              <ChevronLeft size={28} />
            </button>

            <button
              onClick={nextImage}
              className={cx("modalNavButton", "right")}
            >
              <ChevronRight size={28} />
            </button>

            {/* Image Counter in Fullscreen - center */}
            <div className={cx("modalCounter", "center")}>
              {currentIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
